import { ErrorResponse } from 'types/form';
import { callRecaptcha } from './recaptcha';

export enum ApiResources {
  getVIN = 'vehicle/getvin',
  calculatorOffers = 'CalculatorOffers',
  vehicleYears = 'vehicle/getyears',
  vehicleMake = 'vehicle/getmake',
  vehicleModel = 'vehicle/getmodel',
  createContact = 'contacts/contact/create',
  updateContact = 'contacts/contact/update',
  getCreditSummary = 'contacts/',
  saveLoanInformation = 'contacts/contact/save-loan-information',
  getLenderOffers = 'lender',
  proveStart = 'prove/start',
  proveValidate = 'prove/validate',
  proveChallenge = 'prove/challenge',
  proveComplete = 'prove/complete',
  lenderDealAcitve = 'lender',
  getContactByContactId = 'contacts',
  address = 'address',
  customerPortalRedirect = 'contacts/contact/stip-redirect',
  customerPortalDocuments = 'contacts/contact/stip-documents',
  customerPortalMetadata = 'contacts/contact/cp-metadata',
  progressHistory = 'progress-history',
  getProgressHistory = 'progress-history/contact',
  assetIdFromLeadId = 'contacts/lead/',
  contacts = 'contacts',
  employment = 'employment',
  otherIncome = 'otherIncome',
  messageHub = 'cdx-messagehub',
  validateCustomerPortalRedirect = 'contacts/contact/validate-redirect-cdx',
}

export enum Methods {
  get = 'GET',
  patch = 'PATCH',
  post = 'POST',
  put = 'PUT',
}

export const RECAPTCHA_ERROR_MESSAGE = 'Seems like a bot.';
export const FALLBACK_ERROR_MESSAGE = 'Something went wrong, please try again later.';
export const REQUIRED_MONTHS = 24;

export const getUrl = (resource: ApiResources) => `${process.env.REACT_APP_CDX_URL}/${resource}`;

export const createErrorResponse = (status: number | string, message: string): ErrorResponse => {
  return {
    error: {
      status: status || 400,
      message: message.length > 1 ? message : FALLBACK_ERROR_MESSAGE,
    },
  };
};

export const processResponse = async (response: Response) => {
  if (!response.ok) {
    try {
      const errorJson = await response.json();

      if (errorJson?.error || errorJson?.message || errorJson?.errorMessage) {
        const errorMessage =
          errorJson && (errorJson?.message.includes('message') ? JSON.parse(errorJson.message).message : errorJson?.message);

        return {
          error: {
            status: response?.status,
            message: errorMessage || errorJson?.error?.message || errorMessage?.message || response?.statusText,
          },
        };
      }
    } catch {
      return createErrorResponse(response?.status, response?.statusText);
    }

    return createErrorResponse(response?.status, response?.statusText);
  }

  const jsonResponse = await response.json();
  if (response.status > 204 && !jsonResponse.message.includes(RECAPTCHA_ERROR_MESSAGE)) {
    return null;
  }

  if (!jsonResponse) {
    return createErrorResponse(404, FALLBACK_ERROR_MESSAGE);
  }

  return jsonResponse;
};

export const getRequestHeaders = async (): Promise<Record<string, string>> => {
  try {
    const overrideToken = process.env.REACT_APP_RECAPTCHA_OVERRIDE;

    let authToken = '';
    if (!overrideToken) {
      authToken = await callRecaptcha();
    }

    return {
      'Content-Type': 'application/json',
      ...(authToken ? { AuthToken: authToken } : {}),
      ...(overrideToken ? { OverrideToken: overrideToken } : {}),
    };
  } catch (error) {
    console.log('Error generating request headers:', error);
    return {
      'Content-Type': 'application/json',
    };
  }
};

export interface IOption {
  value: string;
  label: string;
}

export const formatOptions = <T, K extends keyof T>(data: T[], valueKey: K, labelKey: K): { value: string; label: string }[] => {
  return data.map((item) => ({
    value: String(item[valueKey]),
    label: String(item[labelKey]),
  }));
};

export const formatPostalCode = (postalCode: string) => {
  if (!postalCode) return '';
  return postalCode.length > 5 ? postalCode.slice(0, 5) : postalCode;
};

export const getContactData = () => {
  try {
    return JSON.parse(localStorage.getItem('iLendingCDX') || '{}')?.contactData ?? null;
  } catch {
    return null;
  }
};
